import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Container, Paper, Typography, TextField, InputAdornment, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, Link } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const AdminEmployeeDocumentsReport = () => {
  const { getAuthHeaders, tokenManager } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const qs = department ? `?department=${encodeURIComponent(department)}` : '';
      const res = await fetch(`${API_URL}/reports/employee-documents${qs}`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) setRows(data.data || []);
      else setRows([]);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => (
      (r.employeeName || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.department || '').toLowerCase().includes(q) ||
      (r.documentName || '').toLowerCase().includes(q) ||
      (r.status || '').toLowerCase().includes(q)
    ));
  }, [rows, search]);

  const handleDownload = async (row) => {
    try {
      const fileName = row.fileName;
      if (!fileName) {
        if (row.link) {
          window.open(row.link, '_blank', 'noopener');
        }
        return;
      }

      const response = await fetch(`${API_URL}/documents/download/${encodeURIComponent(fileName)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (!response.ok) {
        // Fallback to opening raw link if available
        if (row.link) {
          window.open(row.link, '_blank', 'noopener');
        }
        return;
      }

      const blob = await response.blob();

      // Derive filename from header or fallback
      let downloadName = row.documentName || fileName;
      const contentDisposition = response.headers.get('content-disposition') || response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="([^\"]+)"/);
        if (match && match[1]) {
          downloadName = match[1];
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (_) {
      if (row.link) {
        window.open(row.link, '_blank', 'noopener');
      }
    }
  };

  const handleExport = async () => {
    const qs = department ? `?department=${encodeURIComponent(department)}` : '';
    const url = `${API_URL}/reports/employee-documents/export${qs}`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    const blob = await res.blob();
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `employee_documents_report_${Date.now()}.xlsx`;
    link.click();
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Container maxWidth={false} disableGutters>
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Reports / Employee Documents Report
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              placeholder="Search name, email, department, document, status..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              size="small"
              sx={{ minWidth: 280 }}
            />
            <TextField
              placeholder="Filter by department"
              value={department}
              onChange={e => setDepartment(e.target.value)}
              size="small"
              sx={{ minWidth: 220 }}
            />
            <Button variant="outlined" onClick={fetchData} disabled={loading}>
              {loading ? <CircularProgress size={18} /> : 'Apply'}
            </Button>
            <Button variant="contained" onClick={handleExport}>
              Export to Excel
            </Button>
          </Box>

          <Box sx={{ overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Employee Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Document</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Document Link</TableCell>
                  <TableCell>Uploaded At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r, idx) => (
                  <TableRow key={`${r.email}-${r.documentName}-${idx}`}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{r.employeeName}</TableCell>
                    <TableCell>{r.email}</TableCell>
                    <TableCell>{r.department}</TableCell>
                    <TableCell>{r.country}</TableCell>
                    <TableCell>{r.documentName}</TableCell>
                    <TableCell sx={{ color: r.status === 'Uploaded' ? 'success.main' : 'warning.main', fontWeight: 600 }}>{r.status}</TableCell>
                    <TableCell>
                      {r.link ? (
                        <Link component="button" onClick={() => handleDownload(r)}>View</Link>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{r.uploadedAt ? new Date(r.uploadedAt).toLocaleString() : ''}</TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">No records found.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AdminEmployeeDocumentsReport;


